import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Phone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const SHIPPING = [
  ['United Arab Emirates', 'Free', '2–4 days'],
  ['GCC (KSA, Qatar, Kuwait…)', '$15', '3–6 days'],
  ['Europe & UK', '$25', '5–9 days'],
  ['North America', '$30', '6–10 days'],
  ['Rest of world', '$35', '7–14 days'],
];

const CARE_CONTENT = {
  'Delivery & Scheduling': (
    <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
      <p>We ship worldwide from our atelier. At checkout you choose your preferred <strong className="text-foreground">delivery date</strong> and a <strong className="text-foreground">courier window</strong> (morning, afternoon or evening).</p>
      <p>In-stock pieces are dispatched within 1–3 business days. Presale pieces ship on the dispatch date shown on the product page. You'll get an email when your parcel is on its way.</p>
      <div>
        <p className="text-foreground font-medium mb-2">Estimated rates & times</p>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2">Region</th><th>Shipping</th><th>Estimate</th></tr></thead>
          <tbody>
            {SHIPPING.map(([r, c, t]) => (
              <tr key={r} className="border-b border-border">
                <td className="py-2 text-foreground">{r}</td>
                <td className="text-gold font-mono">{c}</td>
                <td>{t}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs mt-2">Duties & taxes, where applicable, are payable on receipt.</p>
      </div>
    </div>
  ),
  'Gift Wrapping': (
    <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
      <p>Add our <strong className="text-foreground">signature gift wrapping</strong> for <strong className="text-foreground">$15</strong> at checkout — a hand-tied box with tissue and a gold seal.</p>
      <p>Include a <strong className="text-foreground">personal message</strong> and we'll hand-write it on a card tucked inside. Prices are never shown on gift orders.</p>
    </div>
  ),
  'Care Guide': (
    <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
      <p>Every Ke-Sa piece is <strong className="text-foreground">gold-plated brass</strong>, finished by hand. To keep the plating beautiful:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Keep pieces dry — remove before showering, swimming or exercise.</li>
        <li>Apply perfume, lotion and hairspray <em>before</em> putting your jewellery on.</li>
        <li>Store in the pouch provided, away from humidity.</li>
        <li>Gently buff with a soft, dry cloth to restore shine — never use chemical polishes.</li>
      </ul>
      <Link to="/care-card" className="inline-block text-gold underline underline-offset-4">Open the printable care card →</Link>
    </div>
  ),
  'Contact': (
    <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
      <p>Our concierge is here to help with sizing, orders and bespoke requests.</p>
      <p>
        <a href="https://instagram.com/_ke_sa" target="_blank" rel="noreferrer" className="text-gold hover:underline">Instagram — @_ke_sa</a><br />
        <a href="https://wa.me/971523577644" target="_blank" rel="noreferrer" className="text-gold hover:underline">WhatsApp — +971 52 357 7644</a>
      </p>
      <p>We reply within one business day.</p>
    </div>
  ),
};

export function Footer() {
  const [topic, setTopic] = useState(null);

  return (
    <footer className="border-t border-border mt-24 bg-surface-elevated/40">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <span className="font-script text-4xl text-gold">Ke-Sa</span>
          <p className="text-sm text-muted-foreground max-w-md leading-relaxed mt-4">
            Handcrafted statement jewellery in gold-plated brass — earrings, rings and cuffs,
            each piece shaped by hand in our atelier and inspired by natural, sculptural forms.
          </p>
          <div className="flex items-center gap-6 mt-6">
            <a href="https://instagram.com/_ke_sa" target="_blank" rel="noreferrer" data-testid="footer-instagram"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors">
              <Instagram size={16} /> @_ke_sa
            </a>
            <a href="tel:+971523577644" data-testid="footer-phone"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors">
              <Phone size={15} /> +971 52 357 7644
            </a>
          </div>
        </div>
        <div>
          <p className="eyebrow mb-4">Shop</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><Link to="/shop" className="hover:text-gold transition-colors">Collection</Link></li>
            <li><Link to="/sets" className="hover:text-gold transition-colors">Matching Sets</Link></li>
            <li><Link to="/presale" className="hover:text-gold transition-colors">Presale</Link></li>
            <li><Link to="/reels" className="hover:text-gold transition-colors">Films</Link></li>
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-4">Client Care</p>
          <ul className="space-y-2 text-sm">
            {Object.keys(CARE_CONTENT).map((k) => (
              <li key={k}>
                <button data-testid={`footer-care-${k.split(' ')[0].toLowerCase()}`} onClick={() => setTopic(k)}
                  className="text-muted-foreground hover:text-gold transition-colors text-left">
                  {k}
                </button>
              </li>
            ))}
            <li><Link to="/faq" data-testid="footer-faq-link" className="text-muted-foreground hover:text-gold transition-colors">FAQ</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border py-6 text-center text-xs text-muted-foreground font-mono tracking-wider">
        © 2026 KE-SA — HANDCRAFTED JEWELLERY
      </div>

      <Dialog open={!!topic} onOpenChange={(o) => !o && setTopic(null)}>
        <DialogContent data-testid="footer-care-dialog" className="bg-surface border-border max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{topic}</DialogTitle></DialogHeader>
          {topic && CARE_CONTENT[topic]}
        </DialogContent>
      </Dialog>
    </footer>
  );
}
